import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { lockClick } from 'directives/cs-capture-cover/cs-capture-cover.directive';
import { LL } from 'i18n/i18n.service';
import { CsPage, NavController } from 'providers/nav-controller';
import { UserDb } from 'services/user-db.service';
import { deepClone } from '@cf-shared/deep-helpers';
import { DatabaseWrapper, DocWatcher } from 'services/database-wrapper';
import { LoggingService } from 'services/logging.service';
import { describeError } from '@cf-shared/error-describe';
import { AnyExercise, CustomExercise, ExerciseDbService } from 'services/exercise-db.service';
import { ExerciseLocation, locateExerciseByIdAsync } from 'services/exercise-resolve';
import { isPouchNotFound } from '@cf-shared/data-model/pouch';
import { equipmentTemplatesEU } from 'pages/equipment/equipment-templates-eu';
import { localizeEquipmentTemplates } from 'pages/equipment/equipment-templates';
import { LoadingController } from 'providers/loading-controller';
import { Equipment } from '@cf-shared/data-model/equipment';
import { ModalPageMarker } from 'providers/modal-page-marker';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { ToastController } from 'providers/toast-controller';
import { ToastType } from 'components/toast/toast-host';
import { IEditExercisePage } from 'pages/edit-exercise/edit-exercise-interface';
import { ViewExercisePageParams } from './view-exercise-interface';
import { isDatabaseUnavailable } from 'services/database-unavailable-error';

// a local custom exercise is the user's own when they shared it from this account — an
// imported copy has its `local` block replaced on import, so it never carries `shared`.
function isOwnSharedExercise(local: AnyExercise | undefined): boolean {
  return !!(local as CustomExercise)?.local?.shared;
}

/**
 * Read-only view of an exercise the user does not own — normally reached from a share link
 * somebody sent them, and also from the "Based on: <name>" line of a fork.
 *
 * This used to be `Mode.View` of the edit page. It was split out because it shares none of
 * that page's machinery: there is no draft, no baseline to compare against, no modified
 * flag, no validation, no strength-standard editing, no image upload and no update-and-diff
 * flow. Keeping both in one class meant every change to the editing behaviour began by
 * establishing that this case did not reach it. See deferred finding `df-7556e3ddb615`.
 *
 * **This page owns the `exercise` URL segment, and the edit page does not.** Share links
 * built before the split point at `/exercise:id~<id>:mode~view`, they are out in the world
 * and cannot be recalled, and one segment resolves to exactly one component. Giving the
 * segment to this page is what makes those links land here directly, with no redirect
 * anywhere in the app. The stray `mode~view` they carry is ignored.
 *
 * There is deliberately no header-button plumbing here. `cs-header` draws the back arrow
 * and pops by itself; the only thing the page owes it is the exercise id, which travels
 * back through `ionViewCanLeave`.
 */
@Component({
    selector: 'page-view-exercise',
    templateUrl: 'view-exercise-page.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ModalPageMarker]
})
export class ViewExercisePage extends CsPage<ViewExercisePageParams, string> implements OnDestroy {
  protected LL = LL;

  protected title = '';
  protected loading = true;
  protected exerciseId: string;
  protected exercise: AnyExercise;
  protected equipmentTemplates: Equipment[];

  protected exerciseNotFound = false;
  /**
   * #4737: set when the exercise could not be READ — the database was shutting down or
   * recovering, a catalog read failed, or the cloud call failed (df-7b992e109846) — as opposed
   * to being genuinely gone. Kept apart from `exerciseNotFound`
   * so the two get different wording; telling a user their exercise no longer exists when we
   * simply could not look is what would make them recreate one they still have.
   */
  protected exerciseUnreadable = false;
  protected isImportableExercise = false;
  protected isOwnExercise = false;

  private docWatcher: DocWatcher;

  constructor(
    public navCtrl: NavController,
    public userDb: UserDb,
    private logging: LoggingService,
    private loadingCtrl: LoadingController,
    private exerciseDB: ExerciseDbService,
    private cloudFunctionsService: CloudFunctionsService,
    private toastController: ToastController,
    private cdRef: ChangeDetectorRef,
  ) {
    super();
  }

  async ionViewCanEnter() {
    this.exerciseId = this.navParams.data.id;

    // the curated-global and local-UDE load branches read through a watcher; the
    // cloud-function fallback path leaves it idle.
    if (!this.docWatcher) {
      this.docWatcher = this.exerciseDB.database.createDocWatcher((_changes) => {
        if (this.viewDestroyed) return;
        this.cdRef.markForCheck();
      });
    }
  }

  async ionViewWillEnter() {
    this.loading = true;
    // #4737: clear both error flags before re-loading. The page is re-entered (returning
    // from a preview, from a "view original" link), and a flag left set by an earlier failed
    // load would keep drawing its card over an exercise that loaded perfectly well the
    // second time.
    this.exerciseNotFound = false;
    this.exerciseUnreadable = false;
    // the same goes for the two banner flags: a banner or Import button left over from an
    // earlier load must not be drawn over this one.
    this.isImportableExercise = false;
    this.isOwnExercise = false;

    await this.loadAsync();
    if (this.exercise) {
      this.title = LL.editExercise.titles.viewExercise({ name: this.exercise.name });
    }
    this.equipmentTemplates = localizeEquipmentTemplates(equipmentTemplatesEU);
    this.loading = false;
    this.cdRef.markForCheck();
  }

  ngOnDestroy() {
    this.docWatcher?.destroy();
  }

  private async loadAsync() {
    try {
      // a fork's "view original" link can target a curated global, a locally-owned UDE, or a
      // share-link UDE that lives only in global_exercises. The lookup order and what counts
      // as a miss live in locateExerciseByIdAsync; what each answer means for this page
      // stays here.
      const located = await locateExerciseByIdAsync(
        this.exerciseId, this.exerciseDB, this.userDb, this.cloudFunctionsService, this.docWatcher);
      if (!located) {
        this.exerciseNotFound = true;
        return;
      }
      this.exercise = located.exercise;
      switch (located.from) {
        case ExerciseLocation.Curated:
          // suppress "Import" — would clone the global with sourceExerciseId === _id and
          // pollute the picker hide-source filter
          this.isImportableExercise = false;
          break;
        case ExerciseLocation.Own:
          // a local copy is either the user's own exercise or one they already imported
          // (import keeps the source _id), so there is nothing to import. Ownership is the
          // same test checkIfOwnExerciseAsync applies: the user shared it from here. Without
          // this, opening a share link to your own exercise drew no banner at all.
          this.isOwnExercise = isOwnSharedExercise(located.exercise);
          this.isImportableExercise = false;
          break;
        case ExerciseLocation.Cloud:
          await this.checkIfOwnExerciseAsync();
          this.isImportableExercise = !this.isOwnExercise;
          break;
      }
    } catch (e) {
      // Do NOT rethrow: that aborts ionViewWillEnter before it clears the loading flag,
      // which is exactly the never-ending spinner of #4737.
      //
      // Only fall back to an error card when there is no exercise to show. The cloud path
      // assigns `this.exercise` and only THEN calls checkIfOwnExerciseAsync, which rethrows
      // anything that is not a not-found — so a failure there arrives here with the exercise
      // already loaded, and flagging it would draw the exercise and a "this is gone" card on
      // the same screen.
      if (!this.exercise) {
        // Different things land in this catch and the user must not be told the same story
        // about all of them. A not-found really does mean the exercise is gone. Anything else
        // means we could not look: a database that is unavailable, a catalog read that timed
        // out, a cloud call that failed. Saying "no longer available" there could send the
        // user off to recreate an exercise they still have (#4737, df-7b992e109846).
        if (isPouchNotFound(e)) {
          this.exerciseNotFound = true;
        } else {
          this.exerciseUnreadable = true;
        }
      }
      // A not-found is an expected state, and so is a database that is shutting down or
      // recovering. Anything else is a real surprise and must reach a future debugger rather
      // than stop at a card the user sees.
      if (!isPouchNotFound(e) && !isDatabaseUnavailable(e)) {
        this.logging.trackAbnormalException(e);
      }
    }
  }

  // check if the viewed exercise exists locally as the user's own shared exercise
  private async checkIfOwnExerciseAsync() {
    try {
      const local = await this.userDb.getCustomExerciseByIdAsync(this.exerciseId);
      if (isOwnSharedExercise(local)) {
        this.isOwnExercise = true;
      }
    } catch (err) {
      if (!isPouchNotFound(err)) throw err;
      // exercise not found locally — user doesn't own it
    }
  }

  /**
   * Hand the exercise id back so a parent's returnCallback (e.g. the picker's
   * `onExerciseModified`) learns which exercise this was. Optional chain because the
   * not-found and load-error paths return without assigning `this.exercise`, and the page
   * stays open on its error card — leaving it has to work from that state too (#4737).
   */
  async ionViewCanLeave(): Promise<{ result: string }> {
    return { result: this.exercise?._id };
  }

  // import the viewed shared exercise into the user's local custom exercises
  handleImportExercise(event: Event) {
    lockClick(event, this.handleImportExerciseAsync());
  }

  private async handleImportExerciseAsync() {
    const loading = this.loadingCtrl.create();
    void loading.present();
    try {
      const imported = deepClone(this.exercise) as CustomExercise;
      // authorHash is preserved on import (Phase 1 of issue #3198) — server-stamped
      // HMAC stays on the local copy for hygiene + audit. It's per-(uid, exerciseId),
      // not per-author, so it does not leak the author's identity.
      // set import tracking. If the published copy carries forwarded lineage (server
      // whitelist preserves sourceExerciseId/sourceRevision through publish), inherit
      // that pointer — the recipient's picker then hides the original global from
      // their picker and "Based on: ..." resolves against it. Legacy pre-PRD shapes
      // (no upstream lineage) fall back to the import-self-reference; picker filter
      // excludes those via the src === _id guard.
      imported.usedInSlots = [];
      const upstreamSourceId = (this.exercise as CustomExercise).local?.sourceExerciseId;
      const upstreamSourceRev = (this.exercise as CustomExercise).local?.sourceRevision;
      if (upstreamSourceId) {
        imported.local = { sourceExerciseId: upstreamSourceId };
        if (upstreamSourceRev) imported.local.sourceRevision = upstreamSourceRev;
      } else {
        imported.local = {
          sourceExerciseId: this.exercise._id,
          sourceRevision: this.exercise._rev,
        };
      }

      // if local exercise with same ID exists, need its _rev for overwrite
      let existing: CustomExercise;
      try {
        existing = await this.userDb.getCustomExerciseByIdAsync(imported._id);
      } catch (err) {
        if (!isPouchNotFound(err)) throw err;
      }
      if (existing) {
        imported._rev = existing._rev;
      } else {
        delete imported._rev;
      }

      const importTx = DatabaseWrapper.getTransactionId();
      await this.userDb.saveCustomExerciseAsync(imported, importTx);

      // Track the SOURCE doc id (the doc in `global_exercises`) on the user's imported-ids
      // singleton. The `coach-exercise-sync` service watches that singleton and restarts
      // Replication B with the updated id list, which makes future author edits flow into
      // the local staging DB and surface the Phase 1 "Update Available" banner.
      // Note: use `this.exercise._id` (the source doc loaded directly from `global_exercises`
      // here), NOT `imported.local.sourceExerciseId` — for legacy import-self-reference
      // shapes that's equal to `imported._id`, which is the local UDE id, not the source-doc id.
      try {
        await this.userDb.addImportedGlobalExerciseIdAsync(this.exercise._id, importTx);
      } catch (err) {
        // the import itself succeeded; only the "Update Available" tracking is missing until
        // a later save of that list catches up, so report without interrupting the person
        this.logging.trackSilentProblem('ImportedExerciseIdTrackFailed', {
          exerciseId: this.exercise._id,
          detail: describeError(err),
        });
      }

      void loading.dismiss();
      this.toastController.showToast({
        message: LL.editExercise.toasts.exerciseImported(),
        type: ToastType.SuccessLight,
        duration: 2000,
      });

      // replace this read-only page with the imported exercise in custom edit mode —
      // pushing would leave a stale read-only copy of the same id behind the editor,
      // which is where "back" would land.
      void this.navCtrl.replaceTopAsync(IEditExercisePage, { id: imported._id });
    } catch (err) {
      void loading.dismiss();
      console.error('Import exercise failed', err);
      this.toastController.showToast({
        message: LL.editExercise.toasts.importFailed(),
        type: ToastType.WarningStrong,
        duration: 3000,
      });
    }
  }
}
